import type { AppToolMeta } from '@awwyeah/shared';
import type * as Y from 'yjs';

/**
 * Pure(-ish) mutation helpers for the `apptools` / `apptools:user:<id>` registries and for
 * mirroring a tool's meta into its own content doc — copied from
 * `apps/web/src/modules/lists/listOps.ts` (WS-15's registry-move pattern), which is the
 * canonical shape this workstream was told to mirror.
 */

export function newId(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function createAppToolMeta(input: {
  kind: AppToolMeta['kind'];
  title: string;
  userId: string;
  emoji?: string;
}): AppToolMeta {
  const at = nowIso();
  return {
    id: newId(),
    kind: input.kind,
    title: input.title,
    emoji: input.emoji,
    createdBy: input.userId,
    createdAt: at,
    updatedBy: input.userId,
    updatedAt: at,
  };
}

/**
 * Moves a tool's *metadata* between the shared `apptools` registry and a member's personal
 * `apptools:user:<id>` one. The tool's content doc (`note:<id>` / `sketch:<id>`) never
 * moves — its ACL row is what actually protects it; the registry move exists purely so its
 * **title** doesn't sit in a doc everyone can open. Write-then-delete, same as
 * `moveListMeta`: the worst case of a crash between the two is the tool briefly appearing
 * in both registries (de-duped personal-wins by the caller), never in neither.
 */
export function moveAppToolMeta(
  from: Y.Map<AppToolMeta>,
  to: Y.Map<AppToolMeta>,
  meta: AppToolMeta,
  userId?: string,
): void {
  if (from === to) return;
  const stamped: AppToolMeta = userId
    ? { ...meta, updatedBy: userId, updatedAt: nowIso() }
    : { ...meta };
  to.set(stamped.id, stamped);
  from.delete(stamped.id);
}

/** Which registry a tool belongs in for a given audience: only `family` lives in the shared one. */
export function registryForAudience(
  audience: 'family' | 'private' | 'members',
): 'family' | 'personal' {
  return audience === 'family' ? 'family' : 'personal';
}

/** Key inside a `note:<id>`/`sketch:<id>` content doc holding a mirror of its `AppToolMeta`. */
export const APPTOOL_META_KEY = 'meta';
const APPTOOL_META_ENTRY = 'tool';

/**
 * Mirrors a tool's metadata into its own content doc (see `mirrorListMeta`'s doc comment
 * for the full rationale — a member it's shared *with* can open the content doc, per the
 * sharing ACL, but never the owner's personal registry, so the title has to live somewhere
 * the ACL itself protects).
 */
export function mirrorAppToolMeta(contentDoc: Y.Doc, meta: AppToolMeta): void {
  contentDoc.getMap<AppToolMeta>(APPTOOL_META_KEY).set(APPTOOL_META_ENTRY, meta);
}

/** Reads the mirror back, or `null` when a doc predates it / was never mirrored. */
export function readAppToolMetaMirror(contentDoc: Y.Doc): AppToolMeta | null {
  const value = contentDoc.getMap<AppToolMeta>(APPTOOL_META_KEY).get(APPTOOL_META_ENTRY);
  return value ?? null;
}
